import { createElement, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { DownloadDarkIcon, TrashDark } from "../../../../../assets";
import { OrganisationTypeConstants } from "../../../../../constants";
import { DeleteDealDocument, downloadDealDocument } from "../../../../../service/fund";
import {
  getDeal,
  searchDealDocumentHistory,
  SearchDealDocumentHistoryRequest,
  SearchDealDocumentHistoryResponse,
} from "../../../../../service/query";
import { ResultData, Status } from "../../../../../service/Shared";
import { initiateDocumentDownloadViaBrowser } from "../../../../../utils";
import { getDealListingRoute, getPipelineTrackerRoute } from "../../../../../utils/routes";
import {
  DataGridColumnDefinition,
  DataGridCursorDataLoadEventHandler,
  DataGridIconCellFormatterData,
  dataGridMapFilterCriteria,
  Toast,
} from "../../../../../widget";

type DealDocumentDetailsState = {
  documentType?: string;
  customVariant?: string;
  from?: string;
};

type DealDocumentDetailsParams = {
  dealUuid?: string;
  dealDocumentUuid?: string;
};

interface UseDealDocumentDetailsReturnData {
  dealName: string;
  isDealLoading: boolean;
  documentType: string;
  documentVariant: string;
  dealUuid?: string;
  dealDocumentUuid?: string;
  historyColumns: DataGridColumnDefinition[];
  historyLoading: boolean;
  historyRefreshToken: boolean;
  setHistoryRefreshToken: Dispatch<SetStateAction<boolean>>;
  showUploadDealDocumentModal: boolean;
  showDeleteDocumentVersionModal: boolean;
  isDeleteDocumentVersionLoading: boolean;
  onHistoryChange: DataGridCursorDataLoadEventHandler;
  openUploadDealDocumentModal: () => void;
  closeUploadDealDocumentModal: () => void;
  confirmDeleteDocumentVersion: () => void;
  cancelDeleteDocumentVersion: () => void;
  handleBackClick: () => void;
}

type DeleteDocumentVersionTarget = {
  dealDocumentHistoryUuid: string;
  rowVersion: number;
};

export const useDealDocumentDetails = (): UseDealDocumentDetailsReturnData => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dealUuid, dealDocumentUuid } = useParams<DealDocumentDetailsParams>();
  const state = location.state as DealDocumentDetailsState | undefined;

  const [dealName, setDealName] = useState<string>("");
  const [isDealLoading, setIsDealLoading] = useState<boolean>(false);
  const [documentType, setDocumentType] = useState<string>(state?.documentType ?? "");
  const [documentVariant, setDocumentVariant] = useState<string>(state?.customVariant ?? "");
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyRefreshToken, setHistoryRefreshToken] = useState<boolean>(false);
  const [showUploadDealDocumentModal, setShowUploadDealDocumentModal] = useState<boolean>(false);
  const [showDeleteDocumentVersionModal, setShowDeleteDocumentVersionModal] = useState<boolean>(false);
  const [deleteDocumentVersionTarget, setDeleteDocumentVersionTarget] = useState<DeleteDocumentVersionTarget | null>(
    null
  );
  const [isDeleteDocumentVersionLoading, setIsDeleteDocumentVersionLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!dealUuid) {
      setIsDealLoading(false);
      return;
    }

    setIsDealLoading(true);
    getDeal({ dealUuid })
      .then((response) => {
        if (response.status === Status.Success && response.data) {
          setDealName(response.data.name ?? "");
        }
      })
      .finally(() => {
        setIsDealLoading(false);
      });
  }, [dealUuid]);

  const historyColumns: DataGridColumnDefinition[] = useMemo(
    () => [
      {
        key: "version",
        name: "Version",
        dataType: "number",
        formatter: "align",
        minWidth: 90,
        alignment: "center",
        filterable: false,
        sortable: true,
      },
      {
        key: "fileName",
        name: "File name",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 200,
        filterable: false,
        sortable: false,
      },
      {
        key: "source",
        name: "Source",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 160,
        filterable: false,
        sortable: false,
      },
      {
        key: "createdAt",
        name: "Date uploaded",
        dataType: "Date",
        formatter: "dateOnly",
        minWidth: 140,
        alignment: "center",
        filterable: false,
        sortable: false,
      },
      {
        key: "createdByUser.fullName",
        name: "Last updated by",
        dataType: "string",
        formatter: "stringWithEllipsis",
        minWidth: 160,
        filterable: false,
        sortable: false,
      },
      {
        key: "downloadIcon",
        name: "Download",
        dataType: "string",
        formatter: "icon",
        alignment: "center",
        minWidth: 90,
        filterable: false,
        sortable: false,
      },
      {
        key: "deleteIcon",
        name: "Delete",
        dataType: "string",
        formatter: "icon",
        alignment: "center",
        minWidth: 90,
        filterable: false,
        sortable: false,
      },
    ],
    []
  );

  const mapDealDocumentHistorySortKey = useCallback(
    (key: string): NonNullable<SearchDealDocumentHistoryRequest["sort"]>[number]["key"] => {
      switch (key) {
        case "version":
          return "results.version";
        case "fileName":
          return "results.file.fileName";
        case "createdByUser.fullName":
          return "results.createdByUser.fullName";
        default:
          return "results.version";
      }
    },
    []
  );

  const openDeleteDocumentVersionModal = useCallback(
    (documentHistory: SearchDealDocumentHistoryResponse["results"][number]): void => {
      const dealDocumentHistoryUuid = documentHistory.uuid;
      if (!dealDocumentHistoryUuid) return;
      setDeleteDocumentVersionTarget({
        dealDocumentHistoryUuid,
        rowVersion: documentHistory.rowVersion ?? 1,
      });
      setShowDeleteDocumentVersionModal(true);
    },
    []
  );

  const cancelDeleteDocumentVersion = useCallback((): void => {
    setShowDeleteDocumentVersionModal(false);
    setDeleteDocumentVersionTarget(null);
  }, []);

  const confirmDeleteDocumentVersion = useCallback(async (): Promise<void> => {
    if (!deleteDocumentVersionTarget) return;

    setIsDeleteDocumentVersionLoading(true);

    try {
      const deleteResponse = await DeleteDealDocument({
        dealDocumentHistoryUuid: deleteDocumentVersionTarget.dealDocumentHistoryUuid,
        dealDocumentUuid: null,
        rowVersion: deleteDocumentVersionTarget.rowVersion,
      });

      if (deleteResponse.status === Status.Success) {
        Toast.success({ message: "Document deleted successfully" });
        setHistoryRefreshToken((prev) => !prev);
        cancelDeleteDocumentVersion();
        return;
      }

      if (deleteResponse.errors?.length) {
        Toast.error({ message: deleteResponse.errors[0].message });
      }
    } catch {
      Toast.error({ message: "Failed to delete document." });
    } finally {
      setIsDeleteDocumentVersionLoading(false);
    }
  }, [cancelDeleteDocumentVersion, deleteDocumentVersionTarget]);

  const onHistoryChange: DataGridCursorDataLoadEventHandler = useCallback(
    async ({ filtering, paging, sorting }) => {
      if (!dealDocumentUuid) {
        return {
          resultData: [],
          paging: {
            pageSize: paging.pageSize,
            totalCount: 0,
            startCursor: "",
            endCursor: "",
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      }

      setHistoryLoading(true);

      try {
        const response = await searchDealDocumentHistory({
          dealDocumentUuid,
          paging: {
            afterCursor: paging.afterCursor ?? null,
            beforeCursor: paging.beforeCursor ?? null,
            limit: paging.pageSize,
          },
          sort:
            sorting.length > 0
              ? sorting.map((sortItem) => ({
                  key: mapDealDocumentHistorySortKey(sortItem.key as string),
                  direction: sortItem.direction,
                }))
              : [
                  {
                    key: "results.version",
                    direction: "desc",
                  },
                ],
          filter: { results: dataGridMapFilterCriteria(filtering) },
        });

        const resultData: ResultData[] =
          response.data?.results?.map((documentHistory: SearchDealDocumentHistoryResponse["results"][number]) => {
            const row = {} as ResultData;
            row.version = documentHistory.version ?? 0;
            row.fileName = documentHistory.file?.fileName ?? "--";
            row.source = "User uploaded";
            row.createdAt = documentHistory.createdAt ? new Date(documentHistory.createdAt) : null;
            row["createdByUser.fullName"] =
              documentHistory.createdByUser?.fullName ?? documentHistory.createdByUser?.email ?? "--";
            row.downloadIcon = {
              action: () => {
                downloadDealDocument({ dealDocumentHistoryUuid: documentHistory.uuid }).then((res: unknown) => {
                  initiateDocumentDownloadViaBrowser(res);
                });
              },
              icon: createElement(DownloadDarkIcon, { width: 20, height: 20 }),
            } as DataGridIconCellFormatterData;
            row.deleteIcon = {
              action: () => {
                openDeleteDocumentVersionModal(documentHistory);
              },
              icon: createElement(TrashDark, { width: 20, height: 20 }),
            } as DataGridIconCellFormatterData;
            row.uuid = documentHistory.file?.uuid ?? `${documentHistory.documentType}-${documentHistory.version}`;
            return row;
          }) ?? [];

        if ((!documentType || !documentVariant) && response.data?.results?.length) {
          const referenceDocument = response.data.results[0];
          if (!documentType && referenceDocument.documentType) {
            setDocumentType(referenceDocument.documentType);
          }
          if (!documentVariant && referenceDocument.customVariant) {
            setDocumentVariant(referenceDocument.customVariant);
          }
        }

        return {
          resultData,
          paging: {
            pageSize: paging.pageSize,
            totalCount: response.data?.paging?.total ?? 0,
            startCursor: response.data?.paging?.startCursor ?? "",
            endCursor: response.data?.paging?.endCursor ?? "",
            hasNextPage: response.data?.paging?.hasNextPage ?? false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage ?? false,
          },
        };
      } finally {
        setHistoryLoading(false);
      }
    },
    [
      dealDocumentUuid,
      documentType,
      documentVariant,
      mapDealDocumentHistorySortKey,
      openDeleteDocumentVersionModal,
      setDocumentType,
      setDocumentVariant,
    ]
  );

  const handleBackClick = useCallback((): void => {
    if (state?.from) {
      navigate(state.from);
      return;
    }

    if (dealUuid) {
      navigate(getDealListingRoute(dealUuid, OrganisationTypeConstants.ASSET_MANAGER));
      return;
    }

    navigate(getPipelineTrackerRoute(OrganisationTypeConstants.ASSET_MANAGER));
  }, [dealUuid, navigate, state?.from]);

  const openUploadDealDocumentModal = useCallback((): void => {
    setShowUploadDealDocumentModal(true);
  }, []);

  const closeUploadDealDocumentModal = useCallback((): void => {
    setShowUploadDealDocumentModal(false);
  }, []);

  return {
    dealName,
    isDealLoading,
    documentType,
    documentVariant,
    dealUuid,
    dealDocumentUuid,
    historyColumns,
    historyLoading,
    historyRefreshToken,
    setHistoryRefreshToken,
    showUploadDealDocumentModal,
    showDeleteDocumentVersionModal,
    isDeleteDocumentVersionLoading,
    onHistoryChange,
    openUploadDealDocumentModal,
    closeUploadDealDocumentModal,
    confirmDeleteDocumentVersion,
    cancelDeleteDocumentVersion,
    handleBackClick,
  };
};
